/*
  ZUMII sticky mobile purchase bar.

  Presentation only. This element owns NO commerce state:

  - Its button is a real `type="submit"` associated with Dawn's canonical
    product form through the HTML `form` attribute, so pressing it fires a
    submit event on that exact form. Dawn's <product-form> handler intercepts
    it and builds FormData from the canonical form - same variant id input,
    same quantity input, same properties. It also degrades to a native form
    POST with JavaScript disabled.
  - Its price is a mirror of the innerHTML Dawn already renders into
    #price-<section-id> from snippets/price.liquid. Dawn re-renders that node
    on every variant change (product-info.js `updateSourceFromDestination`),
    and a MutationObserver copies the result across. No price logic is
    duplicated or recomputed here.
  - Its disabled state and label are mirrored from the canonical
    #ProductSubmitButton-<section-id>, which Dawn owns via
    ProductForm.toggleSubmitButton() and via section re-render.

  Because every source of truth stays in Dawn, the bar cannot drift out of
  sync with the selected variant.
*/

if (!customElements.get('zumii-sticky-atc')) {
  customElements.define(
    'zumii-sticky-atc',
    class ZumiiStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.priceTarget = this.querySelector('[data-zumii-sticky-price]');
        this.button = this.querySelector('[data-zumii-sticky-button]');
        this.buttonText = this.button?.querySelector('[data-zumii-sticky-button-text]');

        this.priceSource = document.getElementById(`price-${this.sectionId}`);
        this.canonicalButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.revealAnchor = this.canonicalButton?.closest('.product-form__buttons') || this.canonicalButton;

        if (!this.canonicalButton || !this.revealAnchor) {
          this.remove();
          return;
        }

        this.syncPrice();
        this.syncButton();
        this.observeSources();
        this.observeVisibility();
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      /* --- mirrors ------------------------------------------------------- */

      syncPrice() {
        if (!this.priceTarget || !this.priceSource) return;
        this.priceTarget.innerHTML = this.priceSource.innerHTML;
      }

      syncButton() {
        if (!this.button || !this.canonicalButton) return;

        const disabled =
          this.canonicalButton.hasAttribute('disabled') ||
          this.canonicalButton.getAttribute('aria-disabled') === 'true';

        this.button.toggleAttribute('disabled', disabled);

        const sourceText = this.canonicalButton.querySelector('span:not(.loading__spinner span)');
        if (this.buttonText && sourceText) {
          const label = sourceText.textContent.trim();
          if (label) this.buttonText.textContent = label;
        }

        this.button.classList.toggle('loading', this.canonicalButton.classList.contains('loading'));
      }

      observeSources() {
        if (this.priceSource) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        }

        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.canonicalButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      /* --- reveal -------------------------------------------------------- */

      observeVisibility() {
        if (!('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Reveal only once the canonical buy area has scrolled out of
              // view, and only when it is above the viewport (i.e. the shopper
              // has moved past it), never before they have reached it.
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.dataset.visible = scrolledPast ? 'true' : 'false';
              this.reserveSpace(scrolledPast);
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );

        this.visibilityObserver.observe(this.revealAnchor);
      }

      // Give the page back exactly the bar's height as bottom padding while it
      // is on screen, so a fixed bar never permanently covers the end of the
      // page. Only applied at mobile widths, where the bar is displayed.
      reserveSpace(active) {
        const height = active ? Math.ceil(this.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty('--zumii-sticky-atc-height', `${height}px`);
      }
    }
  );
}
